import { userToursSeenAtom } from '@/global-store';
import { useAtom } from 'jotai';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ACTIONS, Joyride, LIFECYCLE, STATUS } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import { useTailwindBreakpoint } from '@eye-able/ui';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';
import { T } from '@/i18n/T';

function waitForScrollEnd() {
  let lastChangedFrame = 0;
  let lastX = window.scrollX;
  let lastY = window.scrollY;

  return new Promise((resolve) => {
    function tick(frames) {
      if (frames >= 500 || frames - lastChangedFrame > 20) {
        resolve();
      } else {
        if (window.scrollX != lastX || window.scrollY != lastY) {
          lastChangedFrame = frames;
          lastX = window.scrollX;
          lastY = window.scrollY;
        }
        requestAnimationFrame(tick.bind(null, frames + 1));
      }
    }
    tick(0);
  });
}

function getElementFromStepTarget(stepTarget) {
  if (typeof stepTarget === 'string') {
    return document.querySelector(stepTarget);
  }
  if (stepTarget instanceof HTMLElement) {
    return stepTarget;
  }
  return null;
}

function scrollSidebarTargetIntoView(stepTarget) {
  if (typeof stepTarget !== 'string') {
    return;
  }

  const target = getElementFromStepTarget(stepTarget);
  if (!target) return;

  // Intentionally scroll only the sidebar container.
  // Avoid window scroll jumps causes highlight is not aligned with the element
  const sidebarContent = document.querySelector('[data-sidebar="content"]');
  if (
    sidebarContent instanceof HTMLElement &&
    sidebarContent.contains(target)
  ) {
    const targetRect = target.getBoundingClientRect();
    const sidebarRect = sidebarContent.getBoundingClientRect();
    const centeredTopOffset =
      targetRect.top -
      sidebarRect.top -
      (sidebarRect.height - targetRect.height) / 2;

    sidebarContent.scrollTo({
      top: sidebarContent.scrollTop + centeredTopOffset,
      behavior: 'auto',
    });
  }
}

/**
 * UserTour component to guide users through a set of steps.
 * @param {Object} props - The props object.
 * @param {Object} [props.joyrideProps] - Optional. Props to pass to the Joyride component.
 */
function UserTour({ joyrideProps }) {
  const { t } = useInlineTranslation();
  const steps = useTourSteps();
  const location = useLocation();
  const isDesktop = useTailwindBreakpoint('xl');

  const path = /*isIntroTour ? 'INTRO_TOUR' : */ location.pathname;

  const [userToursSeen, setUserToursSeen] = useAtom(userToursSeenAtom);
  const [runJoyride, setRunJoyride] = useState(false);
  const lastScrolledStep = useRef(null);

  useEffect(() => {
    if (!userToursSeen?.includes(path)) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;

        setTimeout(() => {
          setRunJoyride(true);

          setTimeout(() => {
            const dialog = document.querySelector('.react-joyride__tooltip');

            if (dialog) {
              dialog.focus();
            }
          }, 25);
        }, 25);
      }, 125);
    }
  }, [userToursSeen?.length, path, userToursSeen]);

  if (!isDesktop) return null;

  return (
    <Joyride
      onEvent={(data) => {
        const { status, lifecycle, action, step, index } = data;

        // Scroll once per step index to avoid duplicate sidebar scroll calls.
        if (
          lifecycle === LIFECYCLE.TOOLTIP &&
          index !== lastScrolledStep.current
        ) {
          lastScrolledStep.current = index;
          requestAnimationFrame(() => {
            scrollSidebarTargetIntoView(step?.target);
          });
        }

        if (action === ACTIONS.UPDATE && lifecycle === LIFECYCLE.TOOLTIP) {
          waitForScrollEnd().then(() => {
            window.dispatchEvent(new Event('resize'));
          });
        }

        const nextButton = document.querySelector(
          '.react-joyride__tooltip button[data-action="primary"]',
        );

        // show the steps in the aria label for the next button
        if (nextButton && nextButton.querySelector('span')?.textContent) {
          nextButton.ariaLabel = nextButton.querySelector('span').textContent;
        }

        // check if the tour has been completed by either skipping or finishing it
        if (
          (status === STATUS.FINISHED || status === STATUS.SKIPPED) &&
          lifecycle === LIFECYCLE.COMPLETE
        ) {
          document.body.style.overflow = 'auto';
          setRunJoyride(false);
          setUserToursSeen((prev) =>
            prev.includes(path) ? prev : [...prev, path],
          );
        }
      }}
      continuous
      locale={{
        skip: (
          <strong>
            <T k='skip' />
          </strong>
        ),
        next: t('dashboardGuideNextButton'),
        back: t('dashboardGuideBackButton'),
        last: (
          <span aria-label={t('dashboardGuideLastButtonAria')}>
            <T k='dashboardGuideLastButton' />
          </span>
        ),
        close: t('dashboardGuideCloseButton'),
        open: t('dashboardGuideOpenButton'),
      }}
      run={runJoyride}
      steps={steps}
      options={{
        // Joyride page scrolling conflicts with this scrollable sidebar layout.
        skipScroll: true,
        scrollOffset: 0,
        overlayClickAction: false,
        buttons: ['back', 'primary', 'skip'],
        showProgress: true,
        zIndex: 9000,
        primaryColor: '#000',
      }}
      {...joyrideProps}
    />
  );
}

function useTourSteps() {
  const { t } = useInlineTranslation();

  const isDesktop = useTailwindBreakpoint('xl');

  const showReportWidgets = !!document.querySelector('#homeReportTableSection');

  return useMemo(() => {
    const desktopOnlySteps = [
      {
        target: '#collapse-assist',
        content: t('dashboardGuideHomeStep3'),
        skipBeacon: true,
      },
      {
        target: '#item-audit',
        content: t('dashboardGuideHomeStep4'),
        skipBeacon: true,
      },
      {
        target: '#collapse-report',
        content: t('dashboardGuideHomeStep5'),
        skipBeacon: true,
      },
      {
        target: '#collapse-report-analysis',
        content: t('dashboardGuideHomeStep6'),
        skipBeacon: true,
      },
      {
        target: '#item-ally',
        content: t('dashboardGuideHomeStep8'),
        skipBeacon: true,
      },
      {
        target: '#item-contrast-check',
        content: t('dashboardGuideHomeStep7'),
        skipBeacon: true,
      },
      {
        target: '#support-popup',
        content: t('dashboardGuideHomeStep9'),
        skipBeacon: true,
      },
    ];

    const steps = [
      {
        content: t('dashboardGuideHomeWelcome'),
        title: t('dashboardGuideTitle'),
        placement: 'center',
        target: 'body',
        skipBeacon: true,
      },
      {
        target: '#header-right-section',
        content: t('dashboardGuideHomeStep1'),
        skipBeacon: true,
      },
      showReportWidgets
        ? {
            target: '#homeReportTableSection',
            content: t('dashboardGuideHomeStep2Widget'),
            placement: 'bottom',
            skipBeacon: true,
          }
        : {
            target: '#homeStartUsingSection',
            content: t('dashboardGuideHomeStep2'),
            placement: 'bottom',
            skipBeacon: true,
          },
    ];

    if (isDesktop) {
      steps.push(...desktopOnlySteps);
    } else {
      steps.push({
        target: '#drawer-toggle',
        content: t('dashboardGuideHomeStep10'),
        skipBeacon: true,
      });
    }

    return steps;
  }, [isDesktop, showReportWidgets, t]);
}

UserTour.propTypes = {
  isIntroTour: PropTypes.bool,
  joyrideProps: PropTypes.object,
};

export default UserTour;
